import React, { useEffect, useState, useRef, Children } from 'react';
import { PaystackScript } from '../components/Payment/PaystackScript';
import { Check, Users, Laptop, BookOpen, Zap, Target, Award, Clock, TrendingUp, Code, Lightbulb, ArrowRight, AlertCircle, BarChart, Sparkles, Shield, Monitor, Cpu } from 'lucide-react';
export const AIKids = () => {
  const [selectedPlan, setSelectedPlan] = useState<'earlyBird' | 'regular' | 'sibling' | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'form' | 'success'>('pending');
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [expandedTrack, setExpandedTrack] = useState<number | null>(null);
  // Intersection Observer for scroll animations
  const observerRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    observerRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, {
      threshold: 0.1
    });
    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach(el => observerRef.current?.observe(el));
    return () => observerRef.current?.disconnect();
  }, [paymentStatus]); // Re-run when view changes
  const [formData, setFormData] = useState({
    // Parent/Guardian Info
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    // Child Info
    childName: '',
    childAge: '',
    // Additional
    hasDevice: '',
    internetAccess: '',
    priorExperience: '',
    specialNeeds: ''
  });
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleFormChange = (e: any) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePlanSelect = (plan: 'earlyBird' | 'regular' | 'sibling') => {
    setSelectedPlan(plan);
    setPaymentStatus('form');
    window.scrollTo(0, 0);
  };
  const initializePayment = () => {
    if (!formData.parentName || !formData.parentEmail || !formData.parentPhone || !formData.childName || !formData.childAge) {
      alert('Please fill in all required fields');
      return;
    }
    if (!(window as any).PaystackPop) {
      alert('Payment system loading, please try again in a moment');
      return;
    }
    const amount = selectedPlan === 'earlyBird' ? 650000 : selectedPlan === 'sibling' ? 90000 : 150000;
    const handler = (window as any).PaystackPop.setup({
      key: 'pk_live_c2f71547b29d95243a012d9ef2f8e03485d942e7',
      email: formData.parentEmail,
      amount: amount * 100,
      currency: 'NGN',
      metadata: {
        custom_fields: [{
          display_name: 'Parent Name',
          variable_name: 'parent_name',
          value: formData.parentName
        }, {
          display_name: 'Parent Phone',
          variable_name: 'parent_phone',
          value: formData.parentPhone
        }, {
          display_name: 'Child Name',
          variable_name: 'child_name',
          value: formData.childName
        }, {
          display_name: 'Child Age',
          variable_name: 'child_age',
          value: formData.childAge
        }, {
          display_name: 'Plan',
          variable_name: 'plan',
          value: selectedPlan === 'earlyBird' ? 'Early Bird - ₦650,000 (5 Children)' : selectedPlan === 'sibling' ? 'Sibling Discount - ₦90,000' : 'Regular - ₦150,000'
        }]
      },
      callback: (response: any) => {
        setPaymentStatus('success');
        window.location.href = 'https://chat.whatsapp.com/GoKjklvily1CfnpN12X0C0';
      },
      onClose: () => {
        console.log('Payment closed');
      }
    });
    handler.openIframe();
  };
  const tracks = [{
    icon: <Zap size={32} data-id="element-1734" />,
    title: 'AI Foundations & Kid-Friendly Prompting',
    points: ['How modern AI tools actually work (explained simply)', 'Fun prompt frameworks for homework, creativity, and learning.', 'Learning to "talk" to AI effectively', 'Understanding AI safety and responsible use'],
    color: '#F4B400'
  }, {
    icon: <BarChart size={32} data-id="element-1735" />,
    title: 'AI for Learning & School Projects',
    points: ['Using AI to help with homework and research', 'Creating summaries and study guides', 'Making school projects more creative', 'Learning smarter with AI assistance'],
    color: '#1AB9C8'
  }, {
    icon: <Code size={32} data-id="element-1736" />,
    title: 'AI for Building Websites & Apps',
    points: ['Building simple websites with AI help', 'Creating interactive web layouts and designs', 'Making interactive projects', 'No-code and beginner-friendly tools'],
    color: '#8B5CF6'
  }, {
    icon: <BookOpen size={32} data-id="element-1737" />,
    title: 'AI for Creating Apps & Projects',
    points: ['Turning your ideas into real apps', 'Using AI to design and build', 'Creating fun interactive projects', 'Building without complicated coding'],
    color: '#F4B400'
  }, {
    icon: <Award size={32} data-id="element-1738" />,
    title: 'AI for Creativity & Fun',
    points: ['Making art, music, and stories with AI.', 'Creating cool content for social media', 'Building your creative portfolio', 'Using AI as your creative assistant'],
    color: '#1AB9C8'
  }];
  const faqs = [{
    question: 'Does my child need coding experience?',
    answer: 'No! We start from the very beginning. The program is designed for preteens and teenagers (ages 8-19) with zero coding or AI experience. We teach everything step-by-step in a fun, engaging, age-appropriate way.'
  }, {
    question: 'What device does my child need?',
    answer: 'Your child needs a laptop or desktop computer (Windows or Mac) with a stable internet connection. Minimum requirements: 4GB RAM, modern web browser (Chrome/Firefox/Safari), and a webcam for live classes. Tablets and smartphones are NOT sufficient for this program.'
  }, {
    question: 'What if my child misses a class?',
    answer: 'All classes are recorded and available for lifetime access. Your child can watch the recording and catch up. We also provide class notes, assignments, and one-on-one catch-up support if needed.'
  }, {
    question: 'Is it safe for kids?',
    answer: 'Yes! All sessions are monitored by trained instructors, we use secure kid-safe AI platforms, and parents receive progress updates after each class. All instructors are background-checked and experienced in teaching preteens and teenagers.'
  }, {
    question: 'What age is best for this program?',
    answer: 'The program is designed for preteens and teenagers ages 8-19. We have two tracks: Junior (8-13 years) with more visual and project-based learning, and Senior (14-19 years) with slightly more advanced concepts. Both tracks are beginner-friendly.'
  }, {
    question: 'How much time will my child need?',
    answer: '2 live classes per week (1.5 hours each) + 1-2 hours of fun practice time with projects. Total: 5-6 hours per week for 4 weeks. All practice is project-based and engaging, not boring homework!'
  }, {
    question: "What if my child doesn't learn anything productive?",
    answer: "We offer a 60% money-back guarantee if your child doesn't learn anything productive from the program. We're confident in the value we provide, but we want you to feel secure in your investment."
  }, {
    question: 'Will my child get a certificate?',
    answer: 'Yes! Upon completion, your child receives a digital certificate of completion and their AI projects will be showcased on our website with a dedicated portfolio page that can serve as their personal portfolio. This can be shared with family, friends, schools, and even used for future opportunities!'
  }, {
    question: 'Can siblings join together?',
    answer: 'Absolutely! We offer a special sibling discount - pay ₦90,000 per child (40% off regular price). Siblings can learn together, collaborate on projects, and support each other throughout the program.'
  }, {
    question: 'What happens after the program?',
    answer: 'Your child keeps lifetime access to all recordings, resources, and project templates. They also join our dedicated learning community where they receive continued support, can ask questions, and connect with other young AI creators. We offer advanced follow-up programs for those who want to go deeper into AI and coding.'
  }];
  // Styles for animations and futuristic theme
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

    :root {
      --primary: #0B1F3F;
      --accent-yellow: #F4B400;
      --accent-teal: #1AB9C8;
      --accent-purple: #8B5CF6;
      --bg-light: #F8F9FA;
      --bg-white: #FFFFFF;
      --text-dark: #1A1A1A;
      --text-gray: #4A5568;
    }

    body {
      font-family: 'Inter', sans-serif;
      background-color: var(--bg-light);
      color: var(--text-dark);
      overflow-x: hidden;
    }

    .futuristic-bg {
      background-color: var(--bg-white);
      background-image: 
        radial-gradient(circle at 10% 20%, rgba(244, 180, 0, 0.03) 0%, transparent 20%),
        radial-gradient(circle at 90% 80%, rgba(26, 185, 200, 0.03) 0%, transparent 20%),
        linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%);
      position: relative;
      overflow: hidden;
    }

    .particle {
      position: absolute;
      border-radius: 50%;
      background: linear-gradient(45deg, var(--accent-teal), var(--accent-purple));
      opacity: 0.1;
      animation: float 20s infinite linear;
    }

    .light-beam {
      position: absolute;
      width: 200px;
      height: 1000px;
      background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(26, 185, 200, 0.05) 50%, rgba(255,255,255,0) 100%);
      transform: rotate(45deg);
      animation: beam 15s infinite ease-in-out;
      pointer-events: none;
    }

    .grid-pattern {
      background-image: 
        linear-gradient(rgba(11, 31, 63, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(11, 31, 63, 0.03) 1px, transparent 1px);
      background-size: 40px 40px;
      mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
    }

    .glass-card {
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.5);
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.05);
    }

    .glow-text {
      background: linear-gradient(90deg, var(--primary), var(--accent-teal));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .btn-glow {
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;
      z-index: 1;
    }

    .btn-glow::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
      transition: 0.5s;
      z-index: -1;
    }

    .btn-glow:hover::after {
      left: 100%;
    }

    .btn-glow:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(26, 185, 200, 0.2);
    }

    .card-hover {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .card-hover:hover {
      transform: translateY(-5px) scale(1.01);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
      border-color: var(--accent-teal);
    }

    .scroll-animate {
      opacity: 0;
      transform: translateY(30px);
      transition: all 0.8s cubic-bezier(0.5, 0, 0, 1);
    }

    .scroll-animate.animate-in {
      opacity: 1;
      transform: translateY(0);
    }

    @keyframes float {
      0% { transform: translate(0, 0) rotate(0deg); }
      50% { transform: translate(100px, 50px) rotate(180deg); }
      100% { transform: translate(0, 0) rotate(360deg); }
    }

    @keyframes beam {
      0% { transform: rotate(45deg) translateY(-50%); opacity: 0; }
      50% { opacity: 1; }
      100% { transform: rotate(45deg) translateY(50%); opacity: 0; }
    }

    @keyframes pulse-soft {
      0% { box-shadow: 0 0 0 0 rgba(244, 180, 0, 0.4); }
      70% { box-shadow: 0 0 0 10px rgba(244, 180, 0, 0); }
      100% { box-shadow: 0 0 0 0 rgba(244, 180, 0, 0); }
    }

    .pulse-animation {
      animation: pulse-soft 2s infinite;
    }

    /* Accordion transition */
    .accordion-content {
      transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
      max-height: 0;
      opacity: 0;
      overflow: hidden;
    }
    
    .accordion-content.open {
      max-height: 500px;
      opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    }
  `;
  if (paymentStatus === 'success') {
    return <>
        <style data-id="element-1739">{`{styles}`}</style>
        <div className="min-h-screen flex items-center justify-center px-4 futuristic-bg" data-id="element-1740">
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1741">
            <div className="particle" style={{
            width: '300px',
            height: '300px',
            top: '-50px',
            left: '-50px',
            background: 'radial-gradient(circle, rgba(26,185,200,0.1) 0%, transparent 70%)'
          }} data-id="element-1742"></div>
            <div className="particle" style={{
            width: '200px',
            height: '200px',
            bottom: '10%',
            right: '10%',
            background: 'radial-gradient(circle, rgba(244,180,0,0.1) 0%, transparent 70%)',
            animationDelay: '-5s'
          }} data-id="element-1743"></div>
          </div>

          <div className="text-center p-8 md:p-12 max-w-md glass-card rounded-3xl relative z-10 animate-in fade-in zoom-in duration-500" data-id="element-1744">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center bg-gradient-to-br from-teal-400 to-blue-500 shadow-lg shadow-teal-200/50" data-id="element-1745">
              <Check size={40} className="text-white" data-id="element-1746" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-slate-800" data-id="element-1747">
              Enrollment Successful!
            </h1>
            <p className="text-lg text-slate-600 mb-6" data-id="element-1748">
              Welcome to AI Accelerator for Kids! You've just taken a huge step
              for your child's future.
            </p>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 text-blue-800 text-sm font-medium animate-pulse" data-id="element-1749">
              Redirecting you to our parent community...
            </div>
          </div>
        </div>
      </>;
  }
  if (paymentStatus === 'form') {
    return <>
        <style data-id="element-1750">{`{styles}`}</style>
        <div className="min-h-screen py-12 px-4 futuristic-bg" data-id="element-1751">
          <div className="container mx-auto max-w-2xl relative z-10" data-id="element-1752">
            <div className="mb-8 text-center scroll-animate animate-in" data-id="element-1753">
              <h1 className="text-3xl md:text-4xl font-bold mb-3 text-slate-900" data-id="element-1754">
                Complete Enrollment
              </h1>
              <div className="inline-block px-4 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-800 font-medium" data-id="element-1755">
                {selectedPlan === 'earlyBird' ? 'Early Bird - ₦650,000 (5 Children)' : selectedPlan === 'sibling' ? 'Sibling Discount - ₦90,000' : 'Regular Price - ₦150,000'}
              </div>
            </div>

            <div className="glass-card p-6 md:p-8 rounded-2xl mb-6 border-l-4 border-yellow-400 shadow-lg scroll-animate animate-in" style={{
            animationDelay: '0.1s'
          }} data-id="element-1756">
              <div className="flex items-start gap-4" data-id="element-1757">
                <div className="p-2 bg-yellow-100 rounded-full text-yellow-600 shrink-0" data-id="element-1758">
                  <AlertCircle size={24} data-id="element-1759" />
                </div>
                <div data-id="element-1760">
                  <h3 className="font-bold text-lg text-slate-900 mb-1" data-id="element-1761">
                    Important: Device Requirements
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1762">
                    Your child MUST have access to a laptop or desktop computer
                    (Windows or Mac). Tablets and smartphones are NOT sufficient
                    for this program.
                  </p>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-3xl shadow-xl border border-slate-100 scroll-animate animate-in" style={{
            animationDelay: '0.2s'
          }} data-id="element-1763">
              <h2 className="text-xl font-bold mb-6 text-slate-800 flex items-center gap-2" data-id="element-1764">
                <Users size={20} className="text-teal-500" data-id="element-1765" />
                Parent/Guardian Information
              </h2>

              <div className="space-y-5 mb-8" data-id="element-1766">
                <div className="group" data-id="element-1767">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-teal-600 transition-colors" data-id="element-1768">
                    Parent/Guardian Full Name *
                  </label>
                  <input type="text" name="parentName" value={formData.parentName} onChange={handleFormChange} required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all" placeholder="Enter your full name" data-id="element-1769" />
                </div>

                <div className="group" data-id="element-1770">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-teal-600 transition-colors" data-id="element-1771">
                    Email Address *
                  </label>
                  <input type="email" name="parentEmail" value={formData.parentEmail} onChange={handleFormChange} required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all" placeholder="your.email@example.com" data-id="element-1772" />
                </div>

                <div className="group" data-id="element-1773">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-teal-600 transition-colors" data-id="element-1774">
                    Phone Number *
                  </label>
                  <input type="tel" name="parentPhone" value={formData.parentPhone} onChange={handleFormChange} required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all" placeholder="080XXXXXXXX" data-id="element-1775" />
                </div>
              </div>

              <h2 className="text-xl font-bold mb-6 text-slate-800 flex items-center gap-2 pt-4 border-t border-slate-100" data-id="element-1776">
                <Sparkles size={20} className="text-purple-500" data-id="element-1777" />
                Child Information
              </h2>

              <div className="space-y-5 mb-8" data-id="element-1778">
                <div className="group" data-id="element-1779">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-purple-600 transition-colors" data-id="element-1780">
                    Child's Full Name *
                  </label>
                  <input type="text" name="childName" value={formData.childName} onChange={handleFormChange} required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all" placeholder="Child's full name" data-id="element-1781" />
                </div>

                <div className="group" data-id="element-1782">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-purple-600 transition-colors" data-id="element-1783">
                    Child's Age *
                  </label>
                  <select name="childAge" value={formData.childAge} onChange={handleFormChange} required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all" data-id="element-1784">
                    <option value="" data-id="element-1785">Select age...</option>
                    {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19].map(age => <option key={age} value={age} data-id="element-1786">
                          {age} years old
                        </option>)}
                  </select>
                </div>

                <div className="group" data-id="element-1787">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-purple-600 transition-colors" data-id="element-1788">
                    Does your child have access to a laptop/desktop? *
                  </label>
                  <select name="hasDevice" value={formData.hasDevice} onChange={handleFormChange} required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all" data-id="element-1789">
                    <option value="" data-id="element-1790">Select...</option>
                    <option value="Yes - Windows Laptop" data-id="element-1791">
                      Yes - Windows Laptop
                    </option>
                    <option value="Yes - Mac Laptop" data-id="element-1792">Yes - Mac Laptop</option>
                    <option value="Yes - Desktop Computer" data-id="element-1793">
                      Yes - Desktop Computer
                    </option>
                    <option value="No - Need to arrange" data-id="element-1794">
                      No - Need to arrange
                    </option>
                  </select>
                </div>

                <div className="group" data-id="element-1795">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-purple-600 transition-colors" data-id="element-1796">
                    Internet Access *
                  </label>
                  <select name="internetAccess" value={formData.internetAccess} onChange={handleFormChange} required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all" data-id="element-1797">
                    <option value="" data-id="element-1798">Select...</option>
                    <option value="Stable broadband" data-id="element-1799">Stable broadband</option>
                    <option value="Mobile hotspot" data-id="element-1800">Mobile hotspot</option>
                    <option value="Shared/Limited" data-id="element-1801">Shared/Limited</option>
                  </select>
                </div>

                <div className="group" data-id="element-1802">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-purple-600 transition-colors" data-id="element-1803">
                    Prior coding/tech experience
                  </label>
                  <select name="priorExperience" value={formData.priorExperience} onChange={handleFormChange} className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all" data-id="element-1804">
                    <option value="" data-id="element-1805">Select...</option>
                    <option value="None" data-id="element-1806">None - Complete beginner</option>
                    <option value="Some" data-id="element-1807">Some - Basic computer skills</option>
                    <option value="Moderate" data-id="element-1808">
                      Moderate - Has tried coding before
                    </option>
                  </select>
                </div>

                <div className="group" data-id="element-1809">
                  <label className="block mb-2 font-medium text-sm text-slate-700 group-focus-within:text-purple-600 transition-colors" data-id="element-1810">
                    Any special learning needs? (Optional)
                  </label>
                  <textarea name="specialNeeds" value={formData.specialNeeds} onChange={handleFormChange} rows={3} className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all" placeholder="Let us know if your child has any special learning needs so we can provide better support" data-id="element-1811" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1812">
                <button type="button" onClick={() => {
                setPaymentStatus('pending');
                setSelectedPlan(null);
              }} className="flex-1 py-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all" data-id="element-1813">
                  ← Back
                </button>
                <button onClick={initializePayment} className="flex-1 py-4 rounded-xl font-bold text-white bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 shadow-lg shadow-slate-900/20 transition-all transform hover:-translate-y-1" data-id="element-1814">
                  Pay{' '}
                  {selectedPlan === 'earlyBird' ? '₦650,000' : selectedPlan === 'sibling' ? '₦90,000' : '₦150,000'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>;
  }
  return <>
      <style data-id="element-1815">{`{styles}`}</style>
      <PaystackScript data-id="element-1816" />
      <div className="min-h-screen futuristic-bg" data-id="element-1817">
        {/* Background Elements */}
        <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden" data-id="element-1818">
          <div className="light-beam" style={{
          left: '10%',
          top: '-20%'
        }} data-id="element-1819"></div>
          <div className="light-beam" style={{
          right: '10%',
          top: '10%',
          animationDelay: '-5s'
        }} data-id="element-1820"></div>
          <div className="particle" style={{
          width: '400px',
          height: '400px',
          top: '10%',
          left: '-100px',
          background: 'radial-gradient(circle, rgba(244,180,0,0.05) 0%, transparent 70%)'
        }} data-id="element-1821"></div>
          <div className="particle" style={{
          width: '500px',
          height: '500px',
          bottom: '10%',
          right: '-100px',
          background: 'radial-gradient(circle, rgba(26,185,200,0.05) 0%, transparent 70%)',
          animationDelay: '-10s'
        }} data-id="element-1822"></div>
          <div className="absolute inset-0 grid-pattern opacity-50" data-id="element-1823"></div>
        </div>

        {/* Sticky CTA Bar */}
        {showStickyBar && <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom-full duration-500" data-id="element-1824">
            <div className="container mx-auto max-w-4xl" data-id="element-1825">
              <div className="bg-slate-900/90 backdrop-blur-md text-white p-4 rounded-2xl shadow-2xl border border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1826">
                <div className="flex items-center gap-3" data-id="element-1827">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-1828"></div>
                  <p className="text-sm md:text-base font-medium" data-id="element-1829">
                    Limited to 20 kids per cohort • Early Bird:{' '}
                    <span className="text-yellow-400 font-bold" data-id="element-1830">
                      ₦650,000 (5 Children)
                    </span>
                  </p>
                </div>
                <button onClick={() => handlePlanSelect('earlyBird')} className="w-full sm:w-auto px-6 py-2.5 rounded-xl font-bold text-slate-900 bg-yellow-400 hover:bg-yellow-300 transition-all transform hover:scale-105 shadow-lg shadow-yellow-400/20" data-id="element-1831">
                  Enroll Now
                </button>
              </div>
            </div>
          </div>}

        {/* Hero Section */}
        <section className="relative py-20 md:py-32 px-4 overflow-hidden" data-id="element-1832">
          <div className="container mx-auto relative z-10" data-id="element-1833">
            <div className="max-w-5xl mx-auto text-center" data-id="element-1834">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200 shadow-sm mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-1835">
                <span className="w-2 h-2 rounded-full bg-teal-500" data-id="element-1836"></span>
                <span className="text-xs md:text-sm font-bold tracking-wide text-slate-600 uppercase" data-id="element-1837">
                  For Preteens & Teenagers Ages 8-19
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight text-slate-900 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-100" data-id="element-1838">
                AI Accelerator <br className="hidden md:block" data-id="element-1839" />
                <span className="glow-text" data-id="element-1840">for Kids</span>
              </h1>

              <p className="text-xl md:text-2xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200" data-id="element-1841">
                Empower your child to build the future. A hands-on, 4-week
                program where they create real AI projects — chatbots, websites,
                and creative tools.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300" data-id="element-1842">
                <button onClick={() => handlePlanSelect('earlyBird')} className="btn-glow px-8 py-4 rounded-xl font-bold text-lg text-white bg-slate-900 shadow-xl shadow-slate-900/20" data-id="element-1843">
                  Enroll Your Child
                </button>
                <button onClick={() => document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth'
              })} className="px-8 py-4 rounded-xl font-bold text-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm" data-id="element-1844">
                  View Pricing
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-500" data-id="element-1845">
                {[{
                icon: Users,
                text: 'Ages 8-19'
              }, {
                icon: Zap,
                text: 'No experience needed'
              }, {
                icon: Laptop,
                text: 'Build real projects'
              }, {
                icon: Award,
                text: 'Certificate included'
              }].map((badge, i) => <div key={i} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white/50 backdrop-blur-sm border border-slate-100" data-id="element-1846">
                    <badge.icon size={24} className="text-teal-500" data-id="element-1847" />
                    <span className="text-sm font-medium text-slate-600" data-id="element-1848">
                      {badge.text}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* What It Is Section */}
        <section className="py-20 px-4 relative z-10" data-id="element-1849">
          <div className="container mx-auto" data-id="element-1850">
            <div className="max-w-4xl mx-auto scroll-animate" data-id="element-1851">
              <h2 className="text-3xl md:text-5xl font-bold text-center mb-12 text-slate-900" data-id="element-1852">
                What is the{' '}
                <span className="text-teal-600" data-id="element-1853">AI Accelerator?</span>
              </h2>

              <div className="glass-card p-8 md:p-12 rounded-3xl shadow-xl mb-12" data-id="element-1854">
                <p className="text-xl text-center text-slate-600 mb-8" data-id="element-1855">
                  It's a transformation journey that takes your child from:
                </p>

                <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-lg font-bold" data-id="element-1856">
                  <div className="px-6 py-3 rounded-xl bg-slate-100 text-slate-700 border border-slate-200" data-id="element-1857">
                    AI Curious
                  </div>
                  <ArrowRight className="text-teal-400 rotate-90 md:rotate-0" size={32} data-id="element-1858" />
                  <div className="px-6 py-3 rounded-xl bg-teal-50 text-teal-800 border border-teal-100 shadow-sm" data-id="element-1859">
                    AI Creator
                  </div>
                  <ArrowRight className="text-teal-400 rotate-90 md:rotate-0" size={32} data-id="element-1860" />
                  <div className="px-6 py-3 rounded-xl bg-purple-50 text-purple-800 border border-purple-100 shadow-sm" data-id="element-1861">
                    AI Confident
                  </div>
                </div>
              </div>

              <p className="text-lg md:text-xl text-center max-w-3xl mx-auto text-slate-600 leading-relaxed" data-id="element-1862">
                Your child will learn how to use AI as a{' '}
                <strong className="text-slate-900" data-id="element-1863">creative tool</strong>,{' '}
                <strong className="text-slate-900" data-id="element-1864">learning assistant</strong>,
                and <strong className="text-slate-900" data-id="element-1865">project builder</strong>{' '}
                — all in a fun, safe, age-appropriate environment.
              </p>
            </div>
          </div>
        </section>

        {/* Who This Is For Section */}
        <section className="py-20 px-4 bg-white/50 backdrop-blur-sm relative z-10" data-id="element-1866">
          <div className="container mx-auto" data-id="element-1867">
            <div className="max-w-6xl mx-auto" data-id="element-1868">
              <div className="text-center mb-16 scroll-animate" data-id="element-1869">
                <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900" data-id="element-1870">
                  Who This Program Is For
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1871">
                  Perfect for curious preteens and teenagers (ages 8-19) who are
                  ready to step into the future.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-1872">
                {[{
                title: 'Intentional Parents',
                desc: 'Children whose parents are intentional about preparing them for a tech-driven future',
                image: 'https://cdn.magicpatterns.com/uploads/iNvg2fD9JWszyZkmbbBu4Y/1.png'
              }, {
                title: 'Creative Builders',
                desc: 'Children who are creative, love building things, and want to bring their ideas to life',
                image: 'https://cdn.magicpatterns.com/uploads/knCpr2FBwvFCrreg2NX9of/2.png'
              }, {
                title: 'Future Leaders',
                desc: "Children who aspire to be in the top 1% and lead in tomorrow's AI-powered world",
                image: 'https://cdn.magicpatterns.com/uploads/nB7umKZi7CF3tCsRmhWu5z/3.png'
              }, {
                title: "Tomorrow's Innovators",
                desc: 'Future builders ready to master the skills that will define the next generation',
                image: 'https://cdn.magicpatterns.com/uploads/g9FzTpJcbe2iWmsvRDD5UD/4.png'
              }].map((group, i) => <div key={i} className="group card-hover bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-lg scroll-animate" style={{
                transitionDelay: `${i * 100}ms`
              }} data-id="element-1873">
                    <div className="aspect-square overflow-hidden relative" data-id="element-1874">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 opacity-60 group-hover:opacity-40 transition-opacity" data-id="element-1875"></div>
                      <img src={group.image} alt={group.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" data-id="element-1876" />
                    </div>
                    <div className="p-6 relative z-20 -mt-12" data-id="element-1877">
                      <h3 className="text-xl font-bold mb-2 text-white drop-shadow-md group-hover:text-slate-900 group-hover:drop-shadow-none transition-colors duration-300" data-id="element-1878">
                        {group.title}
                      </h3>
                      <div className="bg-white/95 backdrop-blur rounded-xl p-4 shadow-sm border border-slate-100" data-id="element-1879">
                        <p className="text-sm text-slate-600 leading-relaxed" data-id="element-1880">
                          {group.desc}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="text-center scroll-animate" data-id="element-1881">
                <p className="inline-block px-6 py-3 rounded-full bg-teal-50 text-teal-800 font-bold border border-teal-100" data-id="element-1882">
                  ✨ No prior coding or AI experience required!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Learning Tracks Section */}
        <section className="py-20 px-4 relative z-10" data-id="element-1883">
          <div className="container mx-auto" data-id="element-1884">
            <div className="max-w-4xl mx-auto" data-id="element-1885">
              <div className="text-center mb-16 scroll-animate" data-id="element-1886">
                <h2 className="text-3xl md:text-5xl font-bold mb-4 text-slate-900" data-id="element-1887">
                  5 Core Learning Tracks
                </h2>
                <p className="text-lg text-slate-600" data-id="element-1888">
                  A comprehensive curriculum designed for the next generation
                </p>
              </div>

              <div className="space-y-4" data-id="element-1889">
                {tracks.map((track, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-teal-200 scroll-animate" style={{
                transitionDelay: `${i * 50}ms`
              }} data-id="element-1890">
                    <button onClick={() => setExpandedTrack(expandedTrack === i ? null : i)} className="w-full p-6 flex items-center justify-between text-left focus:outline-none" data-id="element-1891">
                      <div className="flex items-center gap-4 md:gap-6" data-id="element-1892">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110" style={{
                      backgroundColor: `${track.color}15`,
                      color: track.color
                    }} data-id="element-1893">
                          {track.icon}
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-slate-800" data-id="element-1894">
                          {track.title}
                        </h3>
                      </div>
                      <div className={`w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center transition-transform duration-300 ${expandedTrack === i ? 'rotate-180 bg-teal-50 text-teal-600' : 'text-slate-400'}`} data-id="element-1895">
                        <ArrowRight size={18} className="rotate-90" data-id="element-1896" />
                      </div>
                    </button>

                    <div className={`accordion-content bg-slate-50/50 ${expandedTrack === i ? 'open' : ''}`} data-id="element-1897">
                      <div className="px-6 pb-6 pt-2 pl-[5.5rem] md:pl-[6rem]" data-id="element-1898">
                        <ul className="space-y-3" data-id="element-1899">
                          {track.points.map((point, j) => <li key={j} className="flex items-start gap-3 text-slate-600" data-id="element-1900">
                              <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" data-id="element-1901"></div>
                              <span className="text-sm md:text-base" data-id="element-1902">
                                {point}
                              </span>
                            </li>)}
                        </ul>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* How You'll Learn Section */}
        <section className="py-20 px-4 bg-white relative z-10" data-id="element-1903">
          <div className="absolute inset-0 grid-pattern opacity-30" data-id="element-1904"></div>
          <div className="container mx-auto relative" data-id="element-1905">
            <div className="max-w-5xl mx-auto" data-id="element-1906">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1907">
                <div className="scroll-animate" data-id="element-1908">
                  <h2 className="text-3xl md:text-5xl font-bold mb-8 text-slate-900" data-id="element-1909">
                    How Your Child <br data-id="element-1910" />
                    <span className="text-purple-600" data-id="element-1911">Will Learn</span>
                  </h2>

                  <div className="grid gap-4" data-id="element-1912">
                    {['Live, interactive classes (not pre-recorded videos)', 'Fun, hands-on projects every week', 'Small groups (max 20 kids) for personal attention', 'Age-appropriate teaching methods', 'Safe, monitored learning environment', 'All classes recorded for review'].map((item, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-md transition-all duration-300" data-id="element-1913">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0" data-id="element-1914">
                          <Check size={16} data-id="element-1915" />
                        </div>
                        <span className="font-medium text-slate-700" data-id="element-1916">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </div>

                <div className="glass-card p-8 rounded-3xl border border-purple-100 shadow-2xl shadow-purple-100/50 scroll-animate" style={{
                animationDelay: '0.2s'
              }} data-id="element-1917">
                  <h3 className="text-2xl font-bold mb-6 text-slate-900 flex items-center gap-3" data-id="element-1918">
                    <Monitor className="text-purple-500" data-id="element-1919" />
                    Program Format
                  </h3>

                  <div className="space-y-6 relative" data-id="element-1920">
                    <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-slate-200" data-id="element-1921"></div>

                    {[{
                    title: '4-Week Intensive',
                    desc: 'Comprehensive curriculum'
                  }, {
                    title: '8 Live Sessions',
                    desc: '2 classes per week (1.5h each)'
                  }, {
                    title: 'Weekly Projects',
                    desc: 'Build portfolio pieces'
                  }, {
                    title: 'Lifetime Access',
                    desc: 'Recordings & community'
                  }].map((item, i) => <div key={i} className="flex gap-4 relative" data-id="element-1922">
                        <div className="w-8 h-8 rounded-full bg-white border-4 border-purple-100 flex items-center justify-center shrink-0 z-10" data-id="element-1923">
                          <div className="w-2.5 h-2.5 rounded-full bg-purple-500" data-id="element-1924"></div>
                        </div>
                        <div data-id="element-1925">
                          <h4 className="font-bold text-slate-800" data-id="element-1926">
                            {item.title}
                          </h4>
                          <p className="text-sm text-slate-500" data-id="element-1927">{item.desc}</p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Device Requirements */}
        <section className="py-20 px-4 relative z-10" data-id="element-1928">
          <div className="container mx-auto" data-id="element-1929">
            <div className="max-w-4xl mx-auto" data-id="element-1930">
              <div className="bg-yellow-50 rounded-3xl p-8 md:p-12 border border-yellow-100 scroll-animate" data-id="element-1931">
                <div className="flex items-center gap-4 mb-8" data-id="element-1932">
                  <div className="p-3 bg-yellow-100 rounded-xl text-yellow-700" data-id="element-1933">
                    <Laptop size={32} data-id="element-1934" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-1935">
                    Device Requirements
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1936">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100" data-id="element-1937">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-green-700" data-id="element-1938">
                      <Check size={20} data-id="element-1939" /> Required
                    </h3>
                    <ul className="space-y-3 text-sm text-slate-600" data-id="element-1940">
                      <li data-id="element-1941">• Laptop or Desktop (Windows/Mac)</li>
                      <li data-id="element-1942">• Minimum 4GB RAM</li>
                      <li data-id="element-1943">• Modern web browser</li>
                      <li data-id="element-1944">• Webcam & Microphone</li>
                      <li data-id="element-1945">• Stable internet (min 5 Mbps)</li>
                    </ul>
                  </div>

                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 opacity-75" data-id="element-1946">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-red-500" data-id="element-1947">
                      <AlertCircle size={20} data-id="element-1948" /> Not Suitable
                    </h3>
                    <ul className="space-y-3 text-sm text-slate-500" data-id="element-1949">
                      <li data-id="element-1950">• ❌ Tablets (iPad/Android)</li>
                      <li data-id="element-1951">• ❌ Smartphones</li>
                      <li data-id="element-1952">• ❌ Chromebooks</li>
                      <li data-id="element-1953">• ❌ Shared/Limited devices</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 px-4 relative z-10" data-id="element-1954">
          <div className="container mx-auto" data-id="element-1955">
            <div className="max-w-6xl mx-auto" data-id="element-1956">
              <div className="text-center mb-16 scroll-animate" data-id="element-1957">
                <h2 className="text-3xl md:text-5xl font-bold mb-4 text-slate-900" data-id="element-1958">
                  Investment in Their Future
                </h2>
                <p className="text-lg text-slate-600" data-id="element-1959">
                  Limited to 20 kids per cohort • 60% Money-Back Guarantee
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1960">
                {/* Early Bird */}
                <div className="card-hover bg-white rounded-3xl p-8 border border-yellow-200 shadow-xl relative overflow-hidden scroll-animate" data-id="element-1961">
                  <div className="absolute top-0 left-0 w-full h-1 bg-yellow-400" data-id="element-1962"></div>
                  <div className="absolute -right-12 top-6 bg-yellow-400 text-slate-900 text-xs font-bold py-1 px-12 rotate-45 shadow-sm" data-id="element-1963">
                    POPULAR
                  </div>

                  <div className="mb-6" data-id="element-1964">
                    <h3 className="text-lg font-bold text-yellow-600 uppercase tracking-wider mb-2" data-id="element-1965">
                      Early Bird
                    </h3>
                    <div className="flex items-baseline gap-1" data-id="element-1966">
                      <span className="text-4xl font-extrabold text-slate-900" data-id="element-1967">
                        ₦650k
                      </span>
                      <span className="text-slate-500 text-sm" data-id="element-1968">
                        /5 children
                      </span>
                    </div>
                    <p className="text-sm text-green-600 font-medium mt-2" data-id="element-1969">
                      ₦130k per child (Save 13%)
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8" data-id="element-1970">
                    {['Full 4-week program', '8 live interactive classes', 'Certificate + Portfolio', 'Lifetime recording access', 'Community membership'].map((feat, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-600" data-id="element-1971">
                        <Check size={16} className="text-yellow-500 mt-0.5 shrink-0" data-id="element-1972" />
                        {feat}
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('earlyBird')} className="w-full py-3.5 rounded-xl font-bold text-slate-900 bg-yellow-400 hover:bg-yellow-300 transition-colors shadow-lg shadow-yellow-400/20" data-id="element-1973">
                    Enroll Now
                  </button>
                </div>

                {/* Regular */}
                <div className="card-hover bg-white rounded-3xl p-8 border border-slate-200 shadow-lg relative scroll-animate" style={{
                transitionDelay: '100ms'
              }} data-id="element-1974">
                  <div className="mb-6" data-id="element-1975">
                    <h3 className="text-lg font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1976">
                      Regular
                    </h3>
                    <div className="flex items-baseline gap-1" data-id="element-1977">
                      <span className="text-4xl font-extrabold text-slate-900" data-id="element-1978">
                        ₦150k
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mt-2" data-id="element-1979">
                      Standard enrollment
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8" data-id="element-1980">
                    {['Full 4-week program', '8 live interactive classes', 'Certificate + Portfolio', 'Lifetime recording access', 'Community membership'].map((feat, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-600" data-id="element-1981">
                        <Check size={16} className="text-slate-400 mt-0.5 shrink-0" data-id="element-1982" />
                        {feat}
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('regular')} className="w-full py-3.5 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors" data-id="element-1983">
                    Enroll Now
                  </button>
                </div>

                {/* Sibling */}
                <div className="card-hover bg-purple-50 rounded-3xl p-8 border border-purple-200 shadow-lg relative scroll-animate" style={{
                transitionDelay: '200ms'
              }} data-id="element-1984">
                  <div className="mb-6" data-id="element-1985">
                    <h3 className="text-lg font-bold text-purple-600 uppercase tracking-wider mb-2" data-id="element-1986">
                      Sibling Deal
                    </h3>
                    <div className="flex items-baseline gap-1" data-id="element-1987">
                      <span className="text-4xl font-extrabold text-slate-900" data-id="element-1988">
                        ₦90k
                      </span>
                      <span className="text-slate-500 text-sm" data-id="element-1989">/child</span>
                    </div>
                    <p className="text-sm text-purple-600 font-medium mt-2" data-id="element-1990">
                      40% off for siblings
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8" data-id="element-1991">
                    {['Full 4-week program', '8 live interactive classes', 'Certificate + Portfolio', 'Lifetime recording access', 'Community membership'].map((feat, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-600" data-id="element-1992">
                        <Check size={16} className="text-purple-500 mt-0.5 shrink-0" data-id="element-1993" />
                        {feat}
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('sibling')} className="w-full py-3.5 rounded-xl font-bold text-white bg-purple-600 hover:bg-purple-500 transition-colors shadow-lg shadow-purple-600/20" data-id="element-1994">
                    Enroll Sibling
                  </button>
                </div>
              </div>

              <div className="max-w-2xl mx-auto bg-white rounded-2xl p-6 border border-slate-200 text-center shadow-sm scroll-animate" data-id="element-1995">
                <div className="flex items-center justify-center gap-2 mb-2 text-slate-900 font-bold" data-id="element-1996">
                  <Shield className="text-green-500" size={20} data-id="element-1997" />
                  60% Money-Back Guarantee
                </div>
                <p className="text-slate-600 text-sm" data-id="element-1998">
                  If your child doesn't learn anything productive from the
                  program, we'll refund 60% of your payment. We're confident in
                  the value we provide.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 px-4 bg-white relative z-10" data-id="element-1999">
          <div className="container mx-auto" data-id="element-2000">
            <div className="max-w-3xl mx-auto" data-id="element-2001">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-900 scroll-animate" data-id="element-2002">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4" data-id="element-2003">
                {faqs.map((faq, i) => <div key={i} className="bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden transition-all duration-300 hover:bg-white hover:shadow-md scroll-animate" data-id="element-2004">
                    <button onClick={() => setExpandedFAQ(expandedFAQ === i ? null : i)} className="w-full p-6 flex items-center justify-between text-left focus:outline-none" data-id="element-2005">
                      <span className="font-bold text-slate-800 pr-8" data-id="element-2006">
                        {faq.question}
                      </span>
                      <div className={`shrink-0 transition-transform duration-300 ${expandedFAQ === i ? 'rotate-180 text-teal-600' : 'text-slate-400'}`} data-id="element-2007">
                        <ArrowRight size={20} className="rotate-90" data-id="element-2008" />
                      </div>
                    </button>

                    <div className={`accordion-content ${expandedFAQ === i ? 'open' : ''}`} data-id="element-2009">
                      <div className="px-6 pb-6 pt-0 text-slate-600 leading-relaxed border-t border-slate-100/50 mt-2" data-id="element-2010">
                        {faq.answer}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="py-24 px-4 bg-slate-900 text-white relative overflow-hidden" data-id="element-2011">
          <div className="absolute inset-0 opacity-20" data-id="element-2012">
            <div className="particle" style={{
            width: '600px',
            height: '600px',
            top: '-200px',
            right: '-200px',
            background: 'radial-gradient(circle, #1AB9C8 0%, transparent 70%)'
          }} data-id="element-2013"></div>
          </div>

          <div className="container mx-auto relative z-10" data-id="element-2014">
            <div className="max-w-4xl mx-auto text-center scroll-animate" data-id="element-2015">
              <h2 className="text-3xl md:text-5xl font-bold mb-8 leading-tight" data-id="element-2016">
                Why the AI Accelerator <br data-id="element-2017" />
                <span className="text-teal-400" data-id="element-2018">Matters Now</span>
              </h2>

              <div className="grid md:grid-cols-2 gap-12 text-left" data-id="element-2019">
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/10" data-id="element-2020">
                  <p className="text-xl md:text-2xl font-light mb-4" data-id="element-2021">
                    "The future belongs to kids who can{' '}
                    <strong className="text-yellow-400 font-bold" data-id="element-2022">
                      create with AI
                    </strong>
                    , not just consume it."
                  </p>
                </div>
                <div className="flex flex-col justify-center" data-id="element-2023">
                  <p className="text-lg text-slate-300 mb-6" data-id="element-2024">
                    This isn't just another coding class. It's a preparation for
                    the world they will inherit.
                  </p>
                  <p className="text-lg text-slate-300" data-id="element-2025">
                    Give your child the skills, confidence, and creativity to
                    thrive in an AI-powered future.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 bg-yellow-400 relative overflow-hidden" data-id="element-2026">
          <div className="absolute inset-0 opacity-10 grid-pattern" data-id="element-2027"></div>
          <div className="container mx-auto relative z-10" data-id="element-2028">
            <div className="max-w-4xl mx-auto text-center scroll-animate" data-id="element-2029">
              <h2 className="text-3xl md:text-5xl font-extrabold mb-8 text-slate-900" data-id="element-2030">
                Ready to Start Their Journey?
              </h2>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2031">
                <button onClick={() => handlePlanSelect('earlyBird')} className="px-8 py-4 rounded-xl font-bold text-lg text-white bg-slate-900 hover:bg-slate-800 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-2032">
                  Enroll Your Child Now
                </button>
                <button onClick={() => window.location.href = 'https://chat.whatsapp.com/GoKjklvily1CfnpN12X0C0'} className="px-8 py-4 rounded-xl font-bold text-lg text-slate-900 bg-white hover:bg-slate-50 transition-all shadow-lg" data-id="element-2033">
                  Join Waiting List
                </button>
              </div>

              <p className="mt-8 text-sm font-medium text-slate-800 opacity-80" data-id="element-2034">
                Questions? Contact us at hello@thenetworkingsalon.com
              </p>
            </div>
          </div>
        </section>
      </div>
    </>;
};